import { computed } from 'vue';
import { getSimpleChoiceMeta } from '@/utils/getSimpleChoiceMeta/getSimpleChoiceMeta';
import {
  SIMPLE_CHOICE,
  SIMPLE_CHOICE_META,
  type SimpleChoiceOptionGesellschaftenTs,
} from '@/features/ChoicesLists/Simple/types';
import { useInputStore } from '@/features/Sparten';
import { FAHRERKREIS_TYP, KASKOTYP } from '@/features/Sparten/shared/types';
import { ROLE, useClientStore } from '@/features/Clients';
import { wkzWithHsnTsn } from '@/features/Sparten/shared/utils/wkzWithHsnTsn';
import { useGlobalStore } from '@/features/Global/store';
import get from 'lodash/get';
import { useEVBInputStore } from '@/features/EVBService/useEVBInputStore';
import { WKZ } from '@/features/Sparten/shared/types/kfz/types';
import { useSimpleChoicesStore } from '@/features/ChoicesLists/Simple/store';
import { useTariffStore } from '@/features/Tariffs/store';
import { isPkwSparte } from '@/features/Sparten/shared/kfz/isPkwSparte';
import { isKradSparte } from '@/features/Sparten/shared/kfz/isKradSparte';

export const isErstvertragOhneVorversicherung = computed(() =>
  getSimpleChoiceMeta(
    SIMPLE_CHOICE.VERSICHERUNGSART_NEUES_FAHRZEUG,
    SIMPLE_CHOICE_META.VERSICHERUNGSART_IS_ERSTVERTRAG_OHNE_VERSICHERUNG,
  ).includes(useInputStore().allgemein.versicherungsart),
);

export const isNotErstvertragOhneVorversicherung = computed(
  () => !isErstvertragOhneVorversicherung.value,
);

export const visibleVorversicherung = computed(
  () =>
    !getSimpleChoiceMeta(
      SIMPLE_CHOICE.SF_KLASSE_KH,
      SIMPLE_CHOICE_META.SF_KLASSE_KH_NOT_RELEVANT_FOR_VORVERSICHERUNG,
    ).includes(useInputStore().sfKlasseKH) &&
    isNotErstvertragOhneVorversicherung.value,
);

export const isSecondCar = () =>
  useInputStore().allgemein !== undefined &&
  useInputStore().allgemein.versicherungsart !== undefined &&
  getSimpleChoiceMeta(
    SIMPLE_CHOICE.VERSICHERUNGSART_NEUES_FAHRZEUG,
    SIMPLE_CHOICE_META.VERSICHERUNGSART_NEUES_FAHRZEUG_IS_ZWEITWAGEN,
  ).includes(useInputStore().allgemein.versicherungsart);

export const isSecondCarReactive = computed(() => isSecondCar());

export const visibleVorversicherungAndNoZweitwagen = computed(
  () => visibleVorversicherung.value && !isSecondCarReactive.value,
);

export const isAenderungsAntragWithContractTariff = computed(() => {
  const insuranceCompanyInformation = useSimpleChoicesStore().data[
    SIMPLE_CHOICE.GESELLSCHAFTEN_TS
  ].values.find(
    (company) =>
      company.value === useInputStore().vorversicherung.gesellschaftID,
  ) as SimpleChoiceOptionGesellschaftenTs | undefined;

  if (
    insuranceCompanyInformation &&
    insuranceCompanyInformation.insuranceId > 0 &&
    useTariffStore().contractTariff.gesellschaft.id ===
      insuranceCompanyInformation.insuranceId.toString()
  ) {
    return true;
  }

  return false;
});

export const isHalterFirma = computed(() => {
  const halterTypMeta = getSimpleChoiceMeta(
    SIMPLE_CHOICE.HALTER_TYP,
    SIMPLE_CHOICE_META.HALTER_TYP_IS_FIRMA,
  );

  if (halterTypMeta.length === 0) {
    return false;
  }

  return (
    useInputStore().abweichenderHalter.isHalterAbweichend === true &&
    !halterTypMeta.includes(useInputStore().abweichenderHalter.typ)
  );
});

export const isAbweichenderHalterNotInSameHousehold = computed(
  () =>
    useInputStore().abweichenderHalter.isHalterAbweichend === true &&
    !getSimpleChoiceMeta(
      SIMPLE_CHOICE.HALTER_TYP,
      SIMPLE_CHOICE_META.HALTER_TYP_IN_SAME_HOUSEHOLD,
    ).includes(useInputStore().abweichenderHalter.typ),
);

export const abweichenderHalterNotInHaeuslicherGemeinschaftMitVNOrIsFirma =
  computed(
    () =>
      useInputStore().abweichenderHalter.isHalterAbweichend === true &&
      (isAbweichenderHalterNotInSameHousehold.value ||
        useInputStore().versicherungsnehmer.isFirma),
  );

export const versicherungsbeginnLowerLimit = computed(() => {
  if (
    useInputStore().fahrzeug.erstbesitzer === true &&
    useInputStore().fahrzeug.erstzulassung !== null
  ) {
    return useInputStore().fahrzeug.erstzulassung;
  }

  if (useInputStore().fahrzeug.erstmaligeZulassungAufVN !== null) {
    return useInputStore().fahrzeug.erstmaligeZulassungAufVN;
  }

  return new Date();
});

export const isVollkasko = computed(() =>
  getSimpleChoiceMeta(
    SIMPLE_CHOICE.KASKO_TYP,
    SIMPLE_CHOICE_META.KASKO_TYP_VOLLKASKO,
  ).includes(useInputStore().kaskoTyp),
);

export const isNotHaftpflicht = computed(
  () => useInputStore().kaskoTyp !== KASKOTYP.HAFTPFLICHT,
);

export const hasFahrerschutzversicherung = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_FAHRERSCHUTZVERSICHERUNG,
    ).includes(useInputStore().allgemein.wkz),
);

export const hasRabattschutzKfzHaftpflicht = computed(() => {
  if (
    isPkwSparte(useGlobalStore().getSparte) ||
    isKradSparte(useGlobalStore().getSparte)
  ) {
    return useInputStore().kaskoTyp !== KASKOTYP.VOLLKASKO;
  }

  return (
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_RABATTSCHUTZ_HAFTPFLICHT,
    ).includes(useInputStore().allgemein.wkz) &&
    useInputStore().kaskoTyp !== KASKOTYP.VOLLKASKO
  );
});

export const visibleGapDeckung = computed(
  () =>
    isNotHaftpflicht.value &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.FINANZIERUNGSART,
      SIMPLE_CHOICE_META.FINANZIERUNGSART_NOT_SELF_FINANCING,
    ).includes(useInputStore().fahrzeug.finanzierungsart),
);

export const hasRabattschutzKfzVollkasko = computed(() => {
  if (
    isPkwSparte(useGlobalStore().getSparte) ||
    isKradSparte(useGlobalStore().getSparte)
  ) {
    return useInputStore().kaskoTyp === KASKOTYP.VOLLKASKO;
  }

  return (
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_RABATTSCHUTZ_VOLLKASKO,
    ).includes(useInputStore().allgemein.wkz) &&
    useInputStore().kaskoTyp === KASKOTYP.VOLLKASKO
  );
});

export const isSaisonkennzeichen = computed(
  () => useInputStore().isSaisonkennzeichen,
);

export const isVollkaskoAndSecondCar = computed(
  () => isVollkasko.value && isSecondCarReactive.value,
);

export const isVollkaskoAndNotSecondCar = computed(
  () => isVollkasko.value && !isSecondCarReactive.value,
);

export const isNotErstvertragOhneVorversicherungAndNotSecondCar = computed(
  () => isNotErstvertragOhneVorversicherung.value && !isSecondCarReactive.value,
);

export const isErstvertragOhneVorversicherungAndNotSecondCar = computed(
  () => isErstvertragOhneVorversicherung.value && !isSecondCarReactive.value,
);

export const hasMinderjaehrigeKinder = computed(() =>
  getSimpleChoiceMeta(
    SIMPLE_CHOICE.MINOR_CHILDREN,
    SIMPLE_CHOICE_META.MINOR_CHILDREN_HAS_MEHRERE_KINDER,
  ).includes(useInputStore().versicherungsnehmer.hasMinderjaehrigeKinder),
);

export const hasMinderjaehrigesKind = computed(() =>
  getSimpleChoiceMeta(
    SIMPLE_CHOICE.MINOR_CHILDREN,
    SIMPLE_CHOICE_META.MINOR_CHILDREN_EIN_KIND,
  ).includes(useInputStore().versicherungsnehmer.hasMinderjaehrigeKinder),
);

export const hasNotDeutscheNationalitaet = computed(
  () =>
    !getSimpleChoiceMeta(
      SIMPLE_CHOICE.NATIONALITY,
      SIMPLE_CHOICE_META.NATIONALITY_GERMAN,
    ).includes(useClientStore()[ROLE.VERSICHERUNGSNEHMER].data.nationalitaet),
);

export const hasJahreskarteOeffentlicherPersonenverkehr = computed(
  () =>
    useInputStore().versicherungsnehmer
      .hasJahreskarteOeffentlicherPersonenverkehr,
);

export const hasWohneigentum = computed(
  () => useInputStore().versicherungsnehmer.hasWohneigentum,
);

export const hasNotEnteredHsnTsnInformation = computed(
  () =>
    useInputStore().fahrzeug.hsn === '' || useInputStore().fahrzeug.tsn === '',
);

export const hasClientNoFuehrerscheindatum = computed<boolean>(() => {
  const { fuehrerscheindatum } =
    useClientStore()[ROLE.VERSICHERUNGSNEHMER].data;

  return fuehrerscheindatum === null;
});

export const isClientFullAge = computed<boolean>(
  () =>
    hasClientNoFuehrerscheindatum.value &&
    useInputStore().versicherungsnehmer.isFullAge,
);

export const hasVnNotFuehrerscheindatum = computed<boolean>(
  () =>
    useInputStore().versicherungsnehmer.fuehrerscheindatum === null ||
    useInputStore().versicherungsnehmer.fuehrerscheindatum === undefined,
);

export const isVnFullAge = computed<boolean>(
  () =>
    hasVnNotFuehrerscheindatum.value &&
    useInputStore().versicherungsnehmer.isFullAge,
);

export const minZulassungAufVNDate = computed(() => {
  if (
    useInputStore().fahrzeug.erstbesitzer === false &&
    useInputStore().fahrzeug.erstzulassung !== null
  ) {
    return useInputStore().fahrzeug.erstzulassung;
  }

  return new Date();
});

export const hasAssistenzsystem = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_ASSISTENZSYSTEM,
    ).includes(useInputStore().allgemein.wkz),
);

export const hasAnhaengerBauart = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_ANHAENGER_BAUART,
    ).includes(useInputStore().allgemein.wkz),
);

export const hasAufbauart = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_AUFBAUART,
    ).includes(useInputStore().allgemein.wkz),
);

export const hasGesamtewicht = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_GESAMTGEWICHT,
    ).includes(useInputStore().allgemein.wkz),
);

export const hasGefahrgut = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_GEFAHRGUT,
    ).includes(useInputStore().allgemein.wkz),
);

export const hasSfKlasse = () => {
  if (isKradSparte(useGlobalStore().getSparte)) return true;

  return (
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_SF_KLASSE,
    ).includes(useInputStore().allgemein.wkz)
  );
};

export const hasSfKlasseReactive = computed(() => hasSfKlasse());

export const hasNeuwert = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_NEUWERT,
    ).includes(useInputStore().allgemein.wkz),
);

export const hasLeistung = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_LEISTUNG,
    ).includes(useInputStore().allgemein.wkz),
);

export const hasJaehrlicheFahrleistung = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_JAEHRLICHE_FAHRLEISTUNG,
    ).includes(useInputStore().allgemein.wkz),
);

export const hasKilometerstand = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_KILOMETERSTAND,
    ).includes(useInputStore().allgemein.wkz),
);

export const hasNutzungsart = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_NUTZUNGSART,
    ).includes(useInputStore().allgemein.wkz),
);

export const isCompany = computed<boolean>(
  () => useInputStore().versicherungsnehmer.isFirma,
);

export const minimumErstzulassungsdatum = new Date('1900-01-01');

export const isEvbVisible = computed(
  () =>
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.VERSICHERUNGSART_VERSICHERER_TARIF_WECHSEL,
      SIMPLE_CHOICE_META.VERSICHERUNGSART_TARIF_WECHSEL_EVB,
    ).includes(useInputStore().allgemein.versicherungsart) ||
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.VERSICHERUNGSART_NEUES_FAHRZEUG,
      SIMPLE_CHOICE_META.VERSICHERUNGSART_TARIF_WECHSEL_EVB,
    ).includes(useInputStore().allgemein.versicherungsart),
);

export const isAbweichenderHalter = computed(
  () => useInputStore().abweichenderHalter.isHalterAbweichend,
);

export const isAbweichenderHalterInSameHousehold = (
  isHalterAbweichend: boolean,
  typ: string,
) =>
  computed(
    () =>
      isHalterAbweichend &&
      getSimpleChoiceMeta(
        SIMPLE_CHOICE.HALTER_TYP,
        SIMPLE_CHOICE_META.HALTER_TYP_IN_SAME_HOUSEHOLD,
      ).includes(typ),
  );

export const hasHsnTsn = computed(() => {
  if (isPkwSparte(useGlobalStore().getSparte)) return true;

  const wkz = get(useInputStore(), 'allgemein.wkz', undefined);
  return wkzWithHsnTsn.includes(wkz);
});

export const hasNoHsnTsn = computed(() => !hasHsnTsn.value);

export const hasFahrerkreis = computed(
  () =>
    isPkwSparte(useGlobalStore().getSparte) ||
    (useInputStore().allgemein !== undefined &&
      getSimpleChoiceMeta(
        SIMPLE_CHOICE.WKZ,
        SIMPLE_CHOICE_META.KFZ_FAHRERKREIS,
        true,
      ).includes(useInputStore().allgemein.wkz)),
);

export const hasSFKlasseSecondCarKfzNotPkw = computed(
  () => hasSfKlasseReactive.value && isSecondCarReactive.value,
);

export const isAbweichenderHalterCompany = computed(
  () =>
    useEVBInputStore().company !== null &&
    useEVBInputStore().company !== undefined && // required since we use it in the input schema itself
    useEVBInputStore().company.allowAbweichenderHalter === true &&
    useEVBInputStore().abweichenderHalter.isFirma,
);

export const isOpenFahrerkreis = (
  value: boolean,
  typ: FAHRERKREIS_TYP,
): boolean => value && typ === FAHRERKREIS_TYP.OFFEN;

export const hasSfKlasseAndisVollkasko = computed(
  () => hasSfKlasseReactive.value && isVollkasko.value,
);

export const hasHubraum = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_HAS_HUBRAUM,
    ).includes(useInputStore().allgemein.wkz),
);

export const hasZeitwert = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_HAS_ZEITWERT,
    ).includes(useInputStore().allgemein.wkz),
);

export const hasLeergewicht = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_HAS_LEERGEWICHT,
    ).includes(useInputStore().allgemein.wkz),
);

export const hasKradBauart = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.WKZ,
      SIMPLE_CHOICE_META.KFZ_HAS_KRAD_BAUART,
    ).includes(useInputStore().allgemein.wkz),
);

export const isLeichtkraftRadOrRoller = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    (useInputStore().allgemein.wkz === WKZ.LEICHTKRAFTROLLER_50_125_CCM ||
      useInputStore().allgemein.wkz === WKZ.LEICHTKRAFTRAEDER_50_125_CCM),
);

export const isLieferwagen = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    (useInputStore().allgemein.wkz === WKZ.LIEFERWAGEN_WERKVERKEHR ||
      useInputStore().allgemein.wkz === WKZ.LIEFERWAGEN_GEWERBLICHER_VERKEHR),
);

export const isLKW = computed(
  () =>
    useInputStore().allgemein !== undefined &&
    (useInputStore().allgemein.wkz === WKZ.LKW_WERKVERKEHR ||
      useInputStore().allgemein.wkz === WKZ.LKW_GEWERBLICHER_VERKEHR),
);

export const isZweitwagen = computed(() =>
  getSimpleChoiceMeta(
    SIMPLE_CHOICE.VERSICHERUNGSART_NEUES_FAHRZEUG,
    SIMPLE_CHOICE_META.VERSICHERUNGSART_NEUES_FAHRZEUG_IS_ZWEITWAGEN,
  ).includes(useInputStore().allgemein.versicherungsart),
);

export const isCarChange = computed(() =>
  getSimpleChoiceMeta(
    SIMPLE_CHOICE.VERSICHERUNGSART_NEUES_FAHRZEUG,
    SIMPLE_CHOICE_META.VERSICHERUNGSART_NEUES_FAHRZEUG_WECHSEL,
  ).includes(useInputStore().allgemein.versicherungsart),
);

export const isWiederinbetriebnahme = computed(() =>
  getSimpleChoiceMeta(
    SIMPLE_CHOICE.VERSICHERUNGSART_VERSICHERER_TARIF_WECHSEL,
    SIMPLE_CHOICE_META.VERSICHERUNGSART_TARIF_WIEDERINBETRIEBNAHME,
  ).includes(useInputStore().allgemein.versicherungsart),
);
